import React, { useState, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CheckCircle2, Upload as UploadIcon, X as XIcon } from "lucide-react";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import Honeypot from "@/components/Honeypot";
import { siteConfig } from "@/config/siteConfig";
import { COUNTRIES } from "@/data/countries";
import { submitLead, readAttribution, baseMeta, uploadFile } from "@/lib/api";

const PRINTING_TECHNOLOGIES = ["TIJ", "CIJ", "Flexo", "Screen", "Gravure", "Rotogravure", "Offset", "Other"];

const initial = {
  company: "",
  country: "",
  contactName: "",
  email: "",
  phone: "",
  application: "",
  machineBrand: "",
  machineModel: "",
  printingTechnology: "",
  substrate: "",
  currentInk: "",
  currentSupplier: "",
  currentIssue: "",
  monthlyConsumption: "",
};

const Label = ({ htmlFor, required, children }) => (
  <label htmlFor={htmlFor} className="block text-[13px] font-medium text-[var(--charcoal)] mb-1.5">
    {children} {required && <span className="text-[var(--pharma-blue)]">*</span>}
  </label>
);
const inputCls =
  "w-full border border-[var(--border)] rounded-[4px] px-3 py-2.5 text-[14px] text-[var(--charcoal)] bg-white focus:border-[var(--pharma-blue)] focus:outline-none focus:ring-1 focus:ring-[var(--pharma-blue)]";

const FileField = ({ id, label, file, onChange, testid }) => (
  <div>
    <Label htmlFor={id}>{label}</Label>
    <div className="flex items-center gap-3">
      <label
        htmlFor={id}
        className="inline-flex items-center gap-2 border border-[var(--border)] rounded-[4px] px-3 py-2 text-[13px] text-[var(--charcoal)] bg-white cursor-pointer hover:bg-[var(--bg-alt)]"
      >
        <UploadIcon className="w-4 h-4" strokeWidth={1.5} />
        Choose file
      </label>
      <input
        id={id}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
        className="hidden"
        onChange={(e) => onChange(e.target.files?.[0] || null)}
        data-testid={testid}
      />
      {file && (
        <span className="text-[12.5px] text-[var(--slate)] inline-flex items-center gap-2 max-w-[240px] truncate">
          <span className="truncate">{file.name}</span>
          <button
            type="button"
            onClick={() => onChange(null)}
            aria-label="Remove file"
            className="text-[var(--slate)] hover:text-[var(--navy)]"
            data-testid={`${testid}-remove`}
          >
            <XIcon className="w-3.5 h-3.5" />
          </button>
        </span>
      )}
    </div>
    <p className="mt-1.5 text-[11.5px] text-[var(--slate)]">JPG, PNG, WEBP, GIF or PDF, up to 10 MB.</p>
  </div>
);

const FindTheRightInk = () => {
  const [params] = useSearchParams();
  const [form, setForm] = useState(initial);
  const [hp, setHp] = useState("");
  const [labelFile, setLabelFile] = useState(null);
  const [sampleFile, setSampleFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const inflightRef = useRef(false);

  const attribution = useMemo(() => readAttribution(params), [params]);
  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (inflightRef.current) return;
    inflightRef.current = true;
    setError(null);
    setSubmitting(true);
    try {
      let uploadedLabelId = null;
      let uploadedSampleId = null;
      if (labelFile) {
        const up = await uploadFile(labelFile);
        if (!up.ok) throw new Error(up.error || "Label upload failed");
        uploadedLabelId = up.data.id;
      }
      if (sampleFile) {
        const up = await uploadFile(sampleFile);
        if (!up.ok) throw new Error(up.error || "Sample upload failed");
        uploadedSampleId = up.data.id;
      }

      const res = await submitLead({
        type: "TECHNICAL_ENQUIRY",
        source_page: "/find-the-right-ink",
        contact: {
          name: form.contactName,
          company: form.company,
          email: form.email,
          phone: form.phone || null,
          country: form.country,
        },
        attribution,
        meta: baseMeta(),
        payload: {
          application: form.application,
          machine_brand: form.machineBrand || null,
          machine_model: form.machineModel || null,
          printing_technology: form.printingTechnology || null,
          substrate: form.substrate,
          current_ink: form.currentInk || null,
          current_supplier: form.currentSupplier || null,
          issue_or_requirement: form.currentIssue,
          monthly_consumption: form.monthlyConsumption || null,
          uploaded_label_id: uploadedLabelId,
          uploaded_sample_id: uploadedSampleId,
        },
        _hp: hp,
      });
      if (res.ok) {
        setResult(res.data);
      } else if (res.status === 429) {
        setError("You are submitting too frequently. Please try again in a minute.");
      } else if (res.status === 422) {
        setError("Please check the required fields and try again.");
      } else {
        setError("Something went wrong. Please try again shortly.");
      }
    } catch (err) {
      setError(err.message || "Upload or submission failed. Please try again.");
    } finally {
      setSubmitting(false);
      inflightRef.current = false;
    }
  };

  return (
    <>
      <SEO
        rawTitle={`Find the Right Ink for Your Application | ${siteConfig.COMPANY_NAME}`}
        description="Describe your printing application and our team will review the requirement and recommend an appropriate product for evaluation."
        path="/find-the-right-ink"
      />
      <Helmet />

      <div className="mx-auto max-w-[1240px] px-6 lg:px-10 pt-8 lg:pt-10">
        <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Find the Right Ink" }]} />
      </div>

      <section className="mx-auto max-w-[1240px] px-6 lg:px-10 pt-8 lg:pt-10 pb-6" data-testid="fti-hero">
        <p className="eyebrow">Application Advisor</p>
        <h1
          className="font-heading mt-4 text-[32px] leading-tight lg:text-[46px] lg:leading-[1.05] font-semibold text-[var(--navy)]"
          data-testid="fti-h1"
        >
          Find the Right Ink for Your Application
        </h1>
        <p className="mt-6 text-[16px] leading-relaxed text-[var(--charcoal)] max-w-3xl">
          Tell us about your printing application. Our team will review the requirement and recommend a product for evaluation.
        </p>
      </section>

      <section className="mx-auto max-w-[1240px] px-6 lg:px-10 py-8 lg:py-12">
        {result ? (
          <div className="border border-[var(--border)] rounded-[4px] p-8 lg:p-10 bg-[var(--bg-alt)] max-w-3xl" data-testid="fti-success">
            <CheckCircle2 strokeWidth={1.5} style={{ width: 44, height: 44, color: "var(--pharma-blue)" }} aria-hidden="true" />
            <h2 className="font-heading mt-4 text-2xl font-semibold text-[var(--navy)]">
              Thank you. Our team will review your application and contact you to discuss the requirement.
            </h2>
            <p className="mt-6 text-[12px] text-[var(--slate)]" data-testid="fti-reference-id">
              Reference ID: {result.id}
            </p>
          </div>
        ) : (
          <form
            onSubmit={onSubmit}
            className="border border-[var(--border)] rounded-[4px] p-6 lg:p-8 bg-white max-w-4xl"
            data-testid="fti-form"
            noValidate
          >
            {error && (
              <div className="mb-6 border border-[var(--pharma-blue)] rounded-[4px] p-3 bg-[var(--bg-alt)] text-[13.5px] text-[var(--charcoal)]" data-testid="fti-error">
                {error}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="fti-company" required>Company Name</Label>
                <input id="fti-company" name="company" required className={inputCls} value={form.company} onChange={onChange} data-testid="fti-company" />
              </div>
              <div>
                <Label htmlFor="fti-country" required>Country</Label>
                <input list="fti-country-list" id="fti-country" name="country" required className={inputCls} value={form.country} onChange={onChange} data-testid="fti-country" placeholder="Type to search…" />
                <datalist id="fti-country-list">
                  {COUNTRIES.map(([code, name]) => (<option key={code} value={name} />))}
                </datalist>
              </div>
              <div>
                <Label htmlFor="fti-contactName" required>Contact Name</Label>
                <input id="fti-contactName" name="contactName" required className={inputCls} value={form.contactName} onChange={onChange} data-testid="fti-contactName" />
              </div>
              <div>
                <Label htmlFor="fti-email" required>Business Email</Label>
                <input id="fti-email" name="email" type="email" required className={inputCls} value={form.email} onChange={onChange} data-testid="fti-email" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="fti-phone" required>Phone / WhatsApp</Label>
                <input id="fti-phone" name="phone" required className={inputCls} value={form.phone} onChange={onChange} data-testid="fti-phone" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="fti-application" required>Application</Label>
                <input id="fti-application" name="application" required className={inputCls} value={form.application} onChange={onChange} data-testid="fti-application" placeholder="e.g. Blister-pack printing, batch coding on cartons…" />
              </div>
              <div>
                <Label htmlFor="fti-machineBrand">Machine Brand</Label>
                <input id="fti-machineBrand" name="machineBrand" className={inputCls} value={form.machineBrand} onChange={onChange} data-testid="fti-machineBrand" />
              </div>
              <div>
                <Label htmlFor="fti-machineModel">Machine Model</Label>
                <input id="fti-machineModel" name="machineModel" className={inputCls} value={form.machineModel} onChange={onChange} data-testid="fti-machineModel" />
              </div>
              <div>
                <Label htmlFor="fti-printingTechnology">Printing Technology</Label>
                <select id="fti-printingTechnology" name="printingTechnology" className={inputCls} value={form.printingTechnology} onChange={onChange} data-testid="fti-printingTechnology">
                  <option value="">Select technology…</option>
                  {PRINTING_TECHNOLOGIES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <Label htmlFor="fti-substrate" required>Substrate / Material</Label>
                <input id="fti-substrate" name="substrate" required className={inputCls} value={form.substrate} onChange={onChange} data-testid="fti-substrate" />
              </div>
              <div>
                <Label htmlFor="fti-currentInk">Current Ink / Cartridge</Label>
                <input id="fti-currentInk" name="currentInk" className={inputCls} value={form.currentInk} onChange={onChange} data-testid="fti-currentInk" />
              </div>
              <div>
                <Label htmlFor="fti-currentSupplier">Current Supplier</Label>
                <input id="fti-currentSupplier" name="currentSupplier" className={inputCls} value={form.currentSupplier} onChange={onChange} data-testid="fti-currentSupplier" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="fti-currentIssue" required>Current Problem / Requirement</Label>
                <textarea id="fti-currentIssue" name="currentIssue" required rows={5} className={inputCls} value={form.currentIssue} onChange={onChange} data-testid="fti-currentIssue" />
              </div>
              <div>
                <Label htmlFor="fti-monthlyConsumption">Approximate Monthly Consumption</Label>
                <input id="fti-monthlyConsumption" name="monthlyConsumption" className={inputCls} value={form.monthlyConsumption} onChange={onChange} data-testid="fti-monthlyConsumption" />
              </div>
              <div />
              <FileField id="fti-label-upload" label="Upload Existing Ink Label" file={labelFile} onChange={setLabelFile} testid="fti-label-upload" />
              <FileField id="fti-sample-upload" label="Upload Print Sample / Image" file={sampleFile} onChange={setSampleFile} testid="fti-sample-upload" />
            </div>
            <Honeypot value={hp} onChange={(e) => setHp(e.target.value)} />
            <div className="mt-8">
              <button type="submit" disabled={submitting} className="btn-primary text-sm disabled:opacity-70" data-testid="fti-submit">
                {submitting ? "Submitting…" : "Submit"}
              </button>
            </div>
            <p className="mt-6 text-[12px] italic text-[var(--slate)]">
              Product suitability is confirmed through application review.
            </p>
          </form>
        )}
      </section>
    </>
  );
};

export default FindTheRightInk;
